import { randomUUID } from "expo-crypto";
import { z } from "zod";
import { safeAsyncStorage } from "@utils/safeAsyncStorage";
import { auth } from "@utils/firebase";
import { trackOnb } from "./analytics/trackOnboarding";
import { STREAMING_SERVICES, type StreamingService } from "./constants";

const sessionSchema = z.object({
  funnelId: z.string().uuid(),
  attemptId: z.string().uuid().optional(),
  attemptNumber: z.number().int().nonnegative(),
  service: z.enum(STREAMING_SERVICES).optional(),
  createdAt: z.number(),
  openedAt: z.number().optional(),
  status: z.enum(["viewed", "pending", "accepted", "error"]),
});
export type ImportSession = z.infer<typeof sessionSchema>;

export function createImportSession() {
  const key = () =>
    `playlist-import-v1:${auth.currentUser?.uid ?? "anonymous"}`;
  const MAX_AGE = 7 * 24 * 60 * 60 * 1000;
  let cached: { key: string; session: ImportSession } | undefined;

  async function readImportSession(): Promise<ImportSession | undefined> {
    const storageKey = key();
    try {
      const raw =
        cached?.key === storageKey
          ? cached.session
          : JSON.parse((await safeAsyncStorage.getItem(storageKey)) ?? "null");
      const parsed = sessionSchema.safeParse(raw);
      if (parsed.success && Date.now() - parsed.data.createdAt < MAX_AGE)
        return parsed.data;
    } catch {
      // Analytics/retry metadata must never make importing unavailable.
    }
    return undefined;
  }

  async function saveImportSession(session: ImportSession) {
    const storageKey = key();
    cached = { key: storageKey, session };
    try {
      await safeAsyncStorage.setItem(storageKey, JSON.stringify(session));
    } catch {
      // The current screen remains usable even when persistence is unavailable.
    }
  }

  async function viewImportSession() {
    const previous = await readImportSession();
    const session: ImportSession =
      previous && previous.status !== "accepted"
        ? previous
        : {
            funnelId: randomUUID(),
            attemptNumber: 0,
            createdAt: Date.now(),
            status: "viewed",
          };
    await saveImportSession(session);
    trackImportStep(session, "view");
    return session;
  }

  async function beginImportAttempt(
    session: ImportSession,
    service: StreamingService,
  ) {
    const attempt: ImportSession = {
      ...session,
      attemptId: randomUUID(),
      attemptNumber: session.attemptNumber + 1,
      service,
      openedAt: undefined,
      status: "pending",
    };
    await saveImportSession(attempt);
    trackImportStep(attempt, "dsp_selected");
    return attempt;
  }

  function trackImportStep(
    session: ImportSession,
    step: string,
    props: Record<string, unknown> = {},
  ) {
    trackOnb("signup", "import-funnel", step, {
      schema_version: 1,
      funnel_id: session.funnelId,
      attempt_id: session.attemptId,
      attempt_number: session.attemptNumber,
      // This is first/retry within the persisted funnel, not lifetime first use.
      attempt_type: session.attemptNumber > 1 ? "retry" : "first_in_funnel",
      dsp: session.service,
      ...props,
    });
  }

  async function clearImportSession() {
    cached = undefined;
    try {
      await safeAsyncStorage.removeItem(key());
    } catch {
      /* Best effort. */
    }
  }

  return {
    readImportSession,
    saveImportSession,
    viewImportSession,
    beginImportAttempt,
    trackImportStep,
    clearImportSession,
  };
}

export const {
  readImportSession,
  saveImportSession,
  viewImportSession,
  beginImportAttempt,
  trackImportStep,
  clearImportSession,
} = createImportSession();
